import { useCallback, useRef } from "react";
import { ContentLoader } from "@/infrastructure/content/loader";

/** プロセス全体で共有する「先読み済みlevelId」の記録（同じ段へ何度hoverしても再取得しない）。 */
const preloadedLevelIds = new Set<string>();

/**
 * 門の先読みと S-2 入場で同じパース結果を共有するためのローダー。
 * インスタンスを分けると ContentLoader のキャッシュが効かないため、ここで1つに固定する。
 */
const sharedLoader = new ContentLoader();

/**
 * 段への入口（稽古の段の行・門の主の押し）のhover/focusで投機的先読みを開始する（T-02）。クリック前に取得が始まっているよう、
 * `level-N.json` / `stories/level-N.json` の取得だけを`await`せずに開始する
 * （decode()までの完全な先読みや資産の先読みは、実際に段へ入るS-2側の責務。ADR-005）。
 */
export function usePreloadLevel(): (levelId: string) => void {
  const loaderRef = useRef(sharedLoader);

  return useCallback((levelId: string) => {
    if (preloadedLevelIds.has(levelId)) {
      return;
    }
    preloadedLevelIds.add(levelId);
    const loader = loaderRef.current;
    void loader.loadLevel(levelId).catch(() => preloadedLevelIds.delete(levelId));
    void loader.loadLevelStory(levelId).catch(() => preloadedLevelIds.delete(levelId));
  }, []);
}

/** S-2 入場時に門／段一覧の先読み結果を再利用するための共有ローダー。 */
export function getSharedContentLoader(): ContentLoader {
  return sharedLoader;
}

/** テスト専用。先読み済み記録と共有ローダーのキャッシュを消す。 */
export function resetPreloadLevelStateForTests(): void {
  preloadedLevelIds.clear();
  sharedLoader.clearCacheForTests();
}
